import { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAppAuth'

/**
 * Cognito OAuth callback codes that mean "no silent session was available" —
 * i.e. our `prompt=none` probe (see SilentAuthGate) found no live session, or
 * interaction is required. These are expected, not real failures: we route the
 * user to the manual login page rather than showing an error screen.
 */
const SILENT_SIGNIN_MISS = [
  'login_required',
  'interaction_required',
  'consent_required',
  'account_selection_required',
]

function isSilentSigninMiss(err: unknown): boolean {
  if (!err) return false
  const code = typeof err === 'object' && err !== null && 'error' in err
    ? String((err as { error: unknown }).error)
    : ''
  if (SILENT_SIGNIN_MISS.includes(code)) return true
  const message = err instanceof Error ? err.message : String(err)
  return SILENT_SIGNIN_MISS.some((c) => message.includes(c))
}

/**
 * Authentication callback page component that handles OAuth redirect from Cognito
 * Uses react-oidc-context for automatic callback processing and state management
 *
 * Follows react-oidc-context patterns from fix-cognito-sign-in-react-samples.md
 */
export const AuthCallbackPage = (): JSX.Element => {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const hasProcessedCallback = useRef(false)

  // Get authentication state from react-oidc-context
  const auth = useAuth()
  const { isAuthenticated, isLoading, error: authError, oidcProfile } = auth

  useEffect(() => {
    // Wait until react-oidc-context finishes processing the callback. We must
    // NOT mark the callback "processed" while still loading — otherwise the
    // effect that runs once auth completes would skip navigation and leave the
    // user stuck on "Redirecting…". The guard is set only on a terminal action.
    if (isLoading) return
    if (hasProcessedCallback.current) return

    // Handle error state from react-oidc-context
    if (authError) {
      hasProcessedCallback.current = true
      // A `prompt=none` probe that found no live session is expected — send the
      // user to the manual login page instead of an error screen.
      if (isSilentSigninMiss(authError)) {
        console.info('ℹ️ AuthCallbackPage: no silent session, routing to /login')
        navigate('/login', { replace: true })
        return
      }
      console.error('❌ AuthCallbackPage: react-oidc-context error detected:', authError)
      setError('Authentication failed')
      return
    }

    // Handle successful authentication
    if (isAuthenticated && oidcProfile) {
      hasProcessedCallback.current = true
      console.info('✅ AuthCallbackPage: Authentication successful', {
        userId: oidcProfile.profile?.sub,
        email: oidcProfile.profile?.email,
        expired: oidcProfile.expired,
      })
      // Return the user to where they were headed, falling back to the dashboard.
      const returnTo =
        typeof oidcProfile.state === 'object' &&
        oidcProfile.state !== null &&
        'returnTo' in oidcProfile.state
          ? String((oidcProfile.state as { returnTo: unknown }).returnTo)
          : null
      const dest =
        returnTo && returnTo.startsWith('/') && returnTo !== '/login' ? returnTo : '/dashboard'
      console.info(`🚀 AuthCallbackPage: Navigating to ${dest}...`)
      navigate(dest, { replace: true })
      return
    }

    // Loading finished, not authenticated, and no error — the callback did not
    // complete successfully.
    hasProcessedCallback.current = true
    console.error('❌ AuthCallbackPage: Callback completed but user not authenticated')
    setError('Authentication failed - callback did not complete successfully')
  }, [isAuthenticated, isLoading, authError, oidcProfile, navigate])

  // Show loading state while react-oidc-context is processing callback
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Processing Authentication
            </h2>
            <p className="text-gray-600">
              Please wait while we complete your sign-in...
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Show error state if callback processing genuinely failed. A silent-sign-in
  // miss (login_required, etc.) is NOT a real error — the effect above routes it
  // to /login, so fall through to the neutral "redirecting" fallback rather than
  // flashing an error screen.
  if ((authError && !isSilentSigninMiss(authError)) || error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8">
          <div className="text-center">
            <div className="rounded-full bg-red-100 p-3 mx-auto w-16 h-16 flex items-center justify-center mb-4">
              <svg 
                className="w-8 h-8 text-red-600" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M6 18L18 6M6 6l12 12" 
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Authentication Failed
            </h2>
            <p className="text-gray-600 mb-6">
              {error}
            </p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  // This should not be reached due to navigation, but provide fallback
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Authentication Complete
          </h2>
          <p className="text-gray-600">
            Redirecting to dashboard...
          </p>
        </div>
      </div>
    </div>
  )
}
